import { AlertDialog } from "@base-ui/react/alert-dialog";
import { type ReactElement, type ReactNode, useRef, useState } from "react";

import { Button } from "./Button.js";
import { ModalFooter } from "./Modal.js";
import { MODAL_BACKDROP, modalPopupStyles } from "./Modal.styles.js";

export interface ConfirmDialogProps {
  /** The question, naming the action and its cost: "Sell back for $18.40?" */
  title: string;
  /** What happens, in one sentence: "The card leaves your vault and $18.40 goes to your balance." */
  description?: string;
  /** Anything the reader needs to decide: a summary of what goes, an amount. */
  children?: ReactNode;
  /** The action's own words, never "OK" or "Yes": "Sell back", "Cancel order". */
  confirmLabel: string;
  /** The way out, in words that can't be mistaken for the action: "Keep it". */
  cancelLabel?: string;
  /**
   * Runs the action. Return a promise to keep the dialog open, the button busy, until it settles:
   * it closes when the promise resolves and stays open when it rejects (say why in a Toast).
   */
  onConfirm: () => void | Promise<void>;
  /** `danger` for what can't be undone: a danger button, and focus starts on the way out. */
  tone?: "default" | "danger";
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** An element that opens it, e.g. a Button. */
  trigger?: ReactElement;
}

/**
 * Asks before a costly or destructive step: Modal's anatomy with the decisions made once. It is
 * an alert dialog (`role="alertdialog"`): a press outside doesn't dismiss it, Escape and the way
 * out do, the way out comes first, and the action names itself.
 */
export function ConfirmDialog({
  title,
  description,
  children,
  confirmLabel,
  cancelLabel = "Cancel",
  onConfirm,
  tone = "default",
  open: openProp,
  defaultOpen = false,
  onOpenChange,
  trigger,
}: ConfirmDialogProps) {
  const [openState, setOpenState] = useState(defaultOpen);
  const open = openProp ?? openState;
  const [pending, setPending] = useState(false);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);

  const setOpen = (next: boolean) => {
    // While the action runs, the dialog stays: closing it would hide the result.
    if (pending && !next) return;
    setOpenState(next);
    onOpenChange?.(next);
  };

  const confirm = async () => {
    const result = onConfirm();
    if (!(result instanceof Promise)) {
      setOpen(false);
      return;
    }
    setPending(true);
    try {
      await result;
      setPending(false);
      setOpenState(false);
      onOpenChange?.(false);
    } catch {
      setPending(false);
    }
  };

  return (
    <AlertDialog.Root open={open} onOpenChange={setOpen}>
      {trigger && <AlertDialog.Trigger render={trigger} />}
      <AlertDialog.Portal>
        <AlertDialog.Backdrop className={MODAL_BACKDROP} />
        <AlertDialog.Popup
          initialFocus={tone === "danger" ? cancelRef : confirmRef}
          className={modalPopupStyles({ size: "sm" })}
        >
          <div className="flex flex-col gap-1 px-6 pt-6 pb-2">
            <AlertDialog.Title className="font-body text-heading-h1 text-text-primary">
              {title}
            </AlertDialog.Title>
            {description && (
              <AlertDialog.Description className="font-body text-body-b1 text-text-secondary">
                {description}
              </AlertDialog.Description>
            )}
          </div>
          {children && <div className="flex flex-col gap-3 px-6 pt-2 pb-4">{children}</div>}
          <ModalFooter className="mt-4">
            <AlertDialog.Close
              disabled={pending}
              render={
                <Button ref={cancelRef} variant="ghost">
                  {cancelLabel}
                </Button>
              }
            />
            <Button
              ref={confirmRef}
              variant={tone === "danger" ? "danger" : "primary"}
              loading={pending}
              onClick={confirm}
            >
              {confirmLabel}
            </Button>
          </ModalFooter>
        </AlertDialog.Popup>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}
